import React from "react";
import Card from "@mui/material/Card";
import CardContent from "@mui/material/CardContent";
import CardHeader from "@mui/material/CardHeader";
import CircularProgress from "@mui/material/CircularProgress";
import Grid from "@mui/material/Grid";
import Stack from "@mui/material/Stack";
import Switch from "@mui/material/Switch";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import ToggleButton from "@mui/material/ToggleButton";
import ToggleButtonGroup from "@mui/material/ToggleButtonGroup";
import Typography from "@mui/material/Typography";
import { DateTime } from "luxon";
import MonthSelector from "@portal/shared/MonthSelector";
import SelectFarmWarehouse, { getAOCWarehouses } from "@portal/components/SelectFarmWarehouse";
import { numberFormatErrorToZero as numberFormat } from "@portal/utils/numberFunctions";
import { toPercFormat } from "@portal/utils/numberFunctions";
import { getAPIDataParamsReturn } from "@portal/utils/apiFunction";
import { modifyTotalOysters } from "@portal/pages/OysterPL8/utils/onShoreFunction";

import { startOfMonth, startOfNextMonth, textfieldDateFormat, shiftMonth, dateFormat } from "@portal/utils/dateFunctions";

const SLATS = "Slats";
const SLATS_PER_PACK = 100;
const APPROX_OYSTER_PER_SLAT = 120;

const dozToPiece = (num, showDoz, alreadyDoz) => {
    let newNum = num;
    if (showDoz) {
        newNum = alreadyDoz ? num : num / 12;
    } else {
        newNum = alreadyDoz ? num * 12 : num;
    }
    return Number.isFinite(newNum) ? newNum.toFixed(0) : 0;
};

const dateColSet = (batchString) => {
    const opacity = 0.6;
    const col = [
        "rgba(183, 207, 183," + opacity + ")",
        "rgba(199, 219, 218," + opacity + ")",
        "rgba(253, 215, 194," + opacity + ")",
        "rgba(246, 234, 194," + opacity + ")",
        "rgba(181, 234, 214," + opacity + ")",
        "rgba(154, 183, 211," + opacity + ")",
        "rgba(255, 225, 233," + opacity + ")",
    ];
    const match = typeof batchString === "string" ? batchString.match(/[0-9]{4}/) : null;
    if (!match) return "white";
    const yr = Number(match[0]);
    return col[yr % 7];
};

const aggregateGroupData = (items, sortBy) => {
    const grouped = {};
    items.forEach((item) => {
        const baseGroup = item?.cropLabel || "";
        const group1 = sortBy === "By Year" && baseGroup ? baseGroup.substring(0, 4) : baseGroup;
        const group2 = item?.grade?.description || "Unknown";
        let units = Number(item?.totalUnits) || 0;
        let average = Number(item?.average) || 0;
        if (Array.isArray(item?.children) && item.children.length > 0) {
            item.children.forEach((child) => {
                const merged = modifyTotalOysters(
                    { totalUnits: units, average },
                    { totalUnits: child.totalUnits, average: child.average }
                );
                units = Number(merged.totalUnits) || 0;
                average = Number(merged.average) || 0;
            });
        }
        const oysters = units * average;
        const key = `${group1}||${group2}`;
        if (!grouped[key]) {
            grouped[key] = { group1, group2, units: 0, oysters: 0 };
        }
        grouped[key].units += units;
        grouped[key].oysters += oysters;
    });
    return Object.values(grouped);
};

const buildTableData = (items, sortBy) => {
    const aggregated = aggregateGroupData(items, sortBy);
    const batches = {};

    aggregated.forEach(({ group1, group2, units, oysters }) => {
        const count = group2 === SLATS ? units * SLATS_PER_PACK * APPROX_OYSTER_PER_SLAT : oysters;
        if (!batches[group1]) {
            batches[group1] = { batch: group1, grade: [] };
        }
        batches[group1].grade.push({
            description: group2,
            end: count,
            endUnits: units,
        });
    });

    return Object.values(batches)
        .map((b) => ({
            ...b,
            totalEnd: b.grade.reduce((sum, g) => sum + (Number.isFinite(g.end) ? g.end : 0), 0),
        }))
        .sort((a, b) => (a.batch || "").localeCompare(b.batch || ""));
};

const TransfersTable = ({
    monthDate,
    warehouseId,
    allowedFarmList,
    allFarms,
    inDoz,
    sortBy,
    onChangeSort,
    YTD,
    onChangeMonth,
    onToggleYTD,
}) => {
    const [rows, setRows] = React.useState([]);
    const [loading, setLoading] = React.useState(false);

    React.useEffect(() => {
        const abortController = new AbortController();

        const fetchTransfers = async (wid) => {
            let startDate = startOfMonth(monthDate);
            let endDate = startOfNextMonth(monthDate);
            if (YTD) {
                const now = new Date();
                now.setDate(15);
                const endLastMonth = new Date(now);
                let nextDate = shiftMonth(now, -1);
                while (nextDate.getMonth() !== 5) {
                    nextDate = shiftMonth(nextDate, -1);
                }
                startDate = startOfMonth(nextDate);
                endDate = startOfNextMonth(endLastMonth);
            }

            const data = await getAPIDataParamsReturn(
                "/Transfers/GetAllNotCancelled",
                {
                    WarehouseId: wid,
                    StartDate: textfieldDateFormat(startDate),
                    EndDate: textfieldDateFormat(endDate),
                    Source: false,
                },
                abortController
            );
            return Array.isArray(data) ? data : [];
        };

        const load = async () => {
            if ((!warehouseId && !allFarms) || (allFarms && allowedFarmList.length === 0)) {
                setRows([]);
                return;
            }
            setLoading(true);
            try {
                const warehouses = allFarms
                    ? allowedFarmList.map((w) => w.warehouseId).filter(Boolean)
                    : [warehouseId];
                const results = await Promise.all(warehouses.map((wid) => fetchTransfers(wid)));
                const flat = results.flat().filter((t) => !t?.dateCancelled);
                console.log("Fetched transfers", flat);
                setRows(flat);
            } finally {
                setLoading(false);
            }
        };

        load();
        return () => abortController.abort();
    }, [monthDate, warehouseId, allowedFarmList, allFarms, YTD]);

    const organiseData = React.useMemo(() => {
        const dt = rows.filter((d) => d?.destination?.isFarm);
        console.log("Organising data", dt);
        const alltranLines = [];
        dt.forEach((t) => {
            (t.coreTransferLines || []).forEach((ln) => alltranLines.push(ln));
        });
        console.log("All transfer lines", alltranLines);
        const redLines = [];
        if (sortBy === "By Batch") {
            alltranLines.forEach((ln) => {
                const group = ln.group?.cropLabel;
                const grade = ln.group?.grade;
                const qty = (ln.group?.totalUnits || 0) * (ln.group?.average || 0);
                console.log("Processing line", { group, grade, quantity: qty, quantityInDoz: qty / 12 });
                if (!group) return;
                const batch = redLines.find((r) => r.group === group);
                if (batch) {
                    const g = batch.grades.find((x) => x.grade?.id === grade?.id);
                    if (g) {
                        g.quantity += qty;
                    } else {
                        batch.grades.push({ grade, quantity: qty });
                    }
                } else {
                    redLines.push({ group, grades: [{ grade, quantity: qty }] });
                }
            });
        } else {
            const gradeBucket = { group: "Grades", grades: [] };
            alltranLines.forEach((ln) => {
                const grade = ln.group?.grade;
                const qty = (ln.group?.totalUnits || 0) * (ln.group?.average || 0);
                const existing = gradeBucket.grades.find((g) => g.grade?.id === grade?.id);
                if (existing) {
                    existing.quantity += qty;
                } else {
                    gradeBucket.grades.push({ grade, quantity: qty });
                }
            });
            redLines.push(gradeBucket);
        }

        redLines.forEach((r) => {
            r.grades.sort((a, b) => {
                const rank = (desc) => {
                    if (desc === "Cocktail") return 0;
                    if (desc === "Bottle") return 1;
                    if (desc === "Bistro") return 2;
                    if (desc === "Plate") return 3;
                    return 10;
                };
                return rank(a.grade?.description) - rank(b.grade?.description);
            });
        });

        return redLines.sort((a, b) => {
            const aYear = a.group ? a.group.match(/[0-9]{4}/) : null;
            const bYear = b.group ? b.group.match(/[0-9]{4}/) : null;
            if (aYear && bYear) return Number(aYear[0]) - Number(bYear[0]);
            if (aYear) return 1;
            if (bYear) return -1;
            return 0;
        });
    }, [rows, sortBy]);

    const total = organiseData.reduce((sum, d) => sum + d.grades.reduce((gSum, g) => gSum + g.quantity, 0), 0);

    return (
        <Card>
            <CardHeader
                title={`Sales (${inDoz ? "Doz" : "Pcs"})`}
                action={
                    <Stack direction="row" spacing={2} alignItems="center">
                        <ToggleButtonGroup
                            color="primary"
                            exclusive
                            value={sortBy}
                            onChange={(event, val) => val && onChangeSort?.(val)}
                            aria-label="transfer-sort-toggle"
                            size="small"
                        >
                            {["By Batch", "By Grade"].map((opt) => (
                                <ToggleButton key={opt} value={opt}>
                                    <div style={{ whiteSpace: "nowrap" }}>{opt}</div>
                                </ToggleButton>
                            ))}
                        </ToggleButtonGroup>
                        <ToggleButtonGroup
                            color="primary"
                            exclusive
                            value={YTD}
                            onChange={(event, val) => onToggleYTD?.(!!val)}
                            aria-label="transfer-ytd-toggle"
                            size="small"
                        >
                            <ToggleButton value={true}>YTD</ToggleButton>
                        </ToggleButtonGroup>
                        <MonthSelector date={DateTime.fromJSDate(monthDate)} setDate={(dt) => onChangeMonth?.(dt)} compact />
                    </Stack>
                }
            />
            <CardContent>
                {loading ? (
                    <Stack direction="row" spacing={1} alignItems="center">
                        <CircularProgress size={20} />
                        <Typography variant="body2">Loading Sales…</Typography>
                    </Stack>
                ) : (
                    <TableContainer>
                        <Table size="small">
                            <TableBody>
                                {organiseData.length === 0 ? (
                                    <TableRow>
                                        <TableCell colSpan={3}>
                                            <Typography variant="body2" mb={0}>
                                                No sales for this period.
                                            </Typography>
                                        </TableCell>
                                    </TableRow>
                                ) : (
                                    organiseData.map((d) => (
                                        <React.Fragment key={d.group || "grades"}>
                                            <TableRow style={{ backgroundColor: dateColSet(d.group || "") }}>
                                                <TableCell sx={{ pl: 3 }}>
                                                    <Typography variant="h6" mb={0}>
                                                        {d.group || "Grades"}
                                                    </Typography>
                                                </TableCell>
                                                <TableCell align="right" sx={{ pl: 3 }}>
                                                    <Typography variant="h6" mb={0}>
                                                        {numberFormat(dozToPiece(d.grades.reduce((s, g) => s + g.quantity, 0), inDoz, false))}
                                                    </Typography>
                                                </TableCell>
                                                <TableCell align="right" sx={{ pl: 3 }}>
                                                    <Typography variant="h6" mb={0}>
                                                        {toPercFormat(d.grades.reduce((s, g) => s + g.quantity, 0) / (total || 1))}
                                                    </Typography>
                                                </TableCell>
                                            </TableRow>
                                            {d.grades.map((g) => (
                                                <TableRow key={(g.grade?.id || "g") + (g.grade?.description || "")} style={{ backgroundColor: dateColSet(d.group || "") }}>
                                                    <TableCell sx={{ pl: 5 }}>
                                                        <Typography variant="body1" mb={0}>
                                                            {g.grade?.description || "Unknown"}
                                                        </Typography>
                                                    </TableCell>
                                                    <TableCell align="right">
                                                        <Typography variant="body1" mb={0}>
                                                            {numberFormat(dozToPiece(g.quantity, inDoz, false))}
                                                        </Typography>
                                                    </TableCell>
                                                    <TableCell align="right">
                                                        <Typography variant="body1" mb={0}>
                                                            {toPercFormat(g.quantity / (total || 1))}
                                                        </Typography>
                                                    </TableCell>
                                                </TableRow>
                                            ))}
                                        </React.Fragment>
                                    ))
                                )}
                                <TableRow>
                                    <TableCell>
                                        <Typography variant="h5" mb={0} fontWeight={700}>
                                            Total
                                        </Typography>
                                    </TableCell>
                                    <TableCell align="right">
                                        <Typography variant="h5" mb={0} fontWeight={700}>
                                            {numberFormat(dozToPiece(total, inDoz, false))}
                                        </Typography>
                                    </TableCell>
                                    <TableCell />
                                </TableRow>
                            </TableBody>
                        </Table>
                    </TableContainer>
                )}
            </CardContent>
        </Card>
    );
};

const OysterMateTotals = ({ monthDate, warehouseId, allowedFarmList, allFarms, inDoz, sortBy, onChangeSort, renderDateSelector }) => {
    const [rawGroups, setRawGroups] = React.useState([]);
    const [loading, setLoading] = React.useState(false);

    React.useEffect(() => {
        const abortController = new AbortController();

        const fetchGroups = async (wid) => {
            const water = await getAPIDataParamsReturn(
                "/Farms/Groups",
                { WarehouseId: wid },
                abortController
            );
            const onShore = await getAPIDataParamsReturn(
                "/Farms/Groups/OnShore",
                { WarehouseId: wid },
                abortController
            );
            const merged = Array.isArray(water) ? [...water] : [];
            if (Array.isArray(onShore)) {
                onShore.forEach((os) => {
                    const idx = merged.findIndex((m) => m.id === os.id);
                    if (idx >= 0) {
                        merged[idx] = { ...os };
                    } else {
                        merged.push({ ...os });
                    }
                });
            }
            return merged;
        };

        const load = async () => {
            if ((!warehouseId && !allFarms) || (allFarms && allowedFarmList.length === 0)) {
                setRawGroups([]);
                return;
            }
            setLoading(true);
            try {
                const warehouses = allFarms
                    ? allowedFarmList.map((w) => w.warehouseId).filter(Boolean)
                    : [warehouseId];
                const results = await Promise.all(warehouses.map((wid) => fetchGroups(wid)));
                const flat = results.flat();
                setRawGroups(flat);
            } finally {
                setLoading(false);
            }
        };

        load();
        return () => abortController.abort();
    }, [monthDate, warehouseId, allowedFarmList, allFarms]);

    const tableData = React.useMemo(() => buildTableData(rawGroups, sortBy), [rawGroups, sortBy]);

    const showGrade = sortBy === "With Grade";
    const totalOysters = tableData.reduce((sum, b) => sum + b.totalEnd, 0);

    return (
        <Card>
            <CardHeader
                title="Oyster Mate Totals"
                subheader={`As at ${dateFormat(monthDate)}`}
                action={
                    <Stack direction="row" spacing={2} alignItems="center">
                        <ToggleButtonGroup
                            color="primary"
                            exclusive
                            value={sortBy}
                            onChange={(event, val) => val && onChangeSort?.(val)}
                            aria-label="oystermate-sort-toggle"
                            size="small"
                        >
                            {["By Year", "By Batch", "With Grade"].map((opt) => (
                                <ToggleButton key={opt} value={opt}>
                                    <div style={{ whiteSpace: "nowrap" }}>{opt}</div>
                                </ToggleButton>
                            ))}
                        </ToggleButtonGroup>
                    </Stack>
                }
            />
            <CardContent>
                {loading ? (
                    <Stack direction="row" spacing={1} alignItems="center">
                        <CircularProgress size={20} />
                        <Typography variant="body2">Loading totals…</Typography>
                    </Stack>
                ) : (
                    <TableContainer>
                        <Table size="small">
                            <TableHead>
                                <TableRow>
                                    <TableCell>Batch</TableCell>
                                    <TableCell align="right">Quantity {inDoz ? "(doz)" : "(pcs)"}</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {tableData.map((row) => {
                                    const approx = row.grade.find((g) => g.description === SLATS);
                                    return (
                                        <React.Fragment key={row.batch || "unknown"}>
                                            <TableRow style={{ backgroundColor: dateColSet(row.batch) }}>
                                                <TableCell>
                                                    <Typography variant="h6" mb={0} fontWeight={showGrade ? 700 : 400}>
                                                        {row.batch && row.batch.trim() !== "" ? row.batch : "Unknown"}
                                                    </Typography>
                                                </TableCell>
                                                <TableCell align="right">
                                                    <Typography variant="h6" mb={0} fontWeight={showGrade ? 700 : 400}>
                                                        {(approx ? "≈" : "") + numberFormat(dozToPiece(row.totalEnd, inDoz, false))}
                                                    </Typography>
                                                </TableCell>
                                            </TableRow>
                                            {showGrade
                                                ? row.grade.map((g) => (
                                                      <TableRow key={`${row.batch || "unknown"}-${g.description}`} style={{ backgroundColor: dateColSet(row.batch) }}>
                                                          <TableCell>
                                                              <Typography variant="body1" mb={0} sx={{ pl: 4 }}>
                                                                  {g.description}
                                                                  {g.description === SLATS ? " (10 doz/slat approx)" : ""}
                                                              </Typography>
                                                          </TableCell>
                                                          <TableCell align="right">
                                                              <Typography variant="body1" mb={0}>
                                                                  {(g.description === SLATS ? "≈" : "") + numberFormat(dozToPiece(g.end, inDoz, false))}
                                                              </Typography>
                                                          </TableCell>
                                                      </TableRow>
                                                  ))
                                                : null}
                                        </React.Fragment>
                                    );
                                })}
                                <TableRow>
                                    <TableCell>
                                        <Typography variant="h5" mb={0} fontWeight={700}>
                                            Total
                                        </Typography>
                                    </TableCell>
                                    <TableCell align="right">
                                        <Typography variant="h5" mb={0} fontWeight={700}>
                                            {numberFormat(dozToPiece(totalOysters, inDoz, false))}
                                        </Typography>
                                    </TableCell>
                                </TableRow>
                            </TableBody>
                        </Table>
                    </TableContainer>
                )}
            </CardContent>
        </Card>
    );
};

const FarmReports = ({ access, showAll }) => {
    const [startDate, setStartDate] = React.useState(DateTime.now());
    const [jsStartDate, setJsStartDate] = React.useState(new Date());
    const [farm, setFarm] = React.useState(null);
    const [allowedFarmList, setAllowedFarmList] = React.useState([]);
    const [allFarms, setAllFarms] = React.useState(false);
    const [inDoz, setInDoz] = React.useState(true);
    const [sortBy, setSortBy] = React.useState("By Year");
    const [transferSort, setTransferSort] = React.useState("By Batch");
    const [transferMonth, setTransferMonth] = React.useState(DateTime.now());
    const [transferYTD, setTransferYTD] = React.useState(false);

    React.useEffect(() => {
        setJsStartDate(startDate.toJSDate());
    }, [startDate]);

    React.useEffect(() => {
        getAOCWarehouses(access, showAll, setAllowedFarmList);
    }, [access, showAll]);

    React.useEffect(() => {
        if (allowedFarmList.length <= 1 && allFarms) {
            setAllFarms(false);
        }
    }, [allowedFarmList, allFarms]);

    return (
        <Grid container spacing={2} mb={1}>
            <Grid item xs={12} md={6}>
                {!allFarms ? (
                    <SelectFarmWarehouse setState={setFarm} state={farm} access={access} />
                ) : (
                    <Typography variant="h4" mb={0} sx={{ display: "inline" }}>
                        Summary of All Farms
                    </Typography>
                )}
                {allowedFarmList.length > 1 ? (
                    <ToggleButton
                        color="primary"
                        sx={{ ml: 2, mt: { xs: 1, md: 0 } }}
                        selected={allFarms}
                        onChange={() => setAllFarms((prev) => !prev)}
                    >
                        <div style={{ whiteSpace: "nowrap" }}>All Farms</div>
                    </ToggleButton>
                ) : null}
            </Grid>
            <Grid item xs={12} md={6}>
                <Stack direction="row" spacing={2} justifyContent="flex-end" alignItems="center">
                    <Stack direction="row" spacing={1} alignItems="center">
                        <Typography>Pcs</Typography>
                        <Switch checked={inDoz} onChange={(e) => setInDoz(e.target.checked)} />
                        <Typography>Doz</Typography>
                    </Stack>
                </Stack>
            </Grid>

            <Grid item xs={12}>
                <TransfersTable
                    monthDate={React.useMemo(() => transferMonth.toJSDate(), [transferMonth])}
                    warehouseId={farm?.warehouseId}
                    allowedFarmList={allowedFarmList}
                    allFarms={allFarms}
                    inDoz={inDoz}
                    sortBy={transferSort}
                    onChangeSort={setTransferSort}
                    YTD={transferYTD}
                    onChangeMonth={setTransferMonth}
                    onToggleYTD={setTransferYTD}
                />
            </Grid>
            <Grid item xs={12}>
                <OysterMateTotals
                    monthDate={jsStartDate}
                    warehouseId={farm?.warehouseId}
                    allowedFarmList={allowedFarmList}
                    allFarms={allFarms}
                    inDoz={inDoz}
                    sortBy={sortBy}
                    onChangeSort={setSortBy}
                    renderDateSelector={<MonthSelector date={startDate} setDate={setStartDate} compact />}
                />
            </Grid>
        </Grid>
    );
};

export default FarmReports;

